import type { ArrowShape, CellPoint } from '../model/arrow';
import { normalizeBoard, buildArrowCellMap, type BoardData } from '../model/board';
import {
  DIRECTIONS,
  DIRECTION_VECTORS,
  oppositeDirection,
  type Direction,
} from '../model/direction';
import type { LevelRecipe } from '../model/recipe';
import type { Pcg32 } from './prng';
import {
  computeCellStatics,
  getCellWeightFn,
  type CellWeightFn,
  type StrategyContext,
} from './strategies';
import type { Mask } from './stencil/mask';

/**
 * Reverse construction for winding arrows.
 *
 * Each arrow is grown *backwards from its head*: first a head cell and a facing whose exit
 * corridor is completely clear, then a tail that wanders away from it through free cells.
 * Because the corridor was empty at the moment of insertion, the arrow was legal then — and
 * removing arrows in reverse insertion order returns the board to exactly that state. The
 * reversed insertion order is therefore a proven solution, which is what makes every
 * generated board solvable by construction.
 *
 * Two invariants keep the result playable:
 *   - the corridor is checked against *all* occupied cells, so no arrow is ever blocked by
 *     an arrow that was already there;
 *   - the growing tail may not enter its own corridor, so an arrow can never trap itself.
 *
 * A useful consequence: the arrow inserted last is never blocked, so a constructed board
 * can never open with zero legal moves.
 */

/** No arrow is ever shorter than this: a lone head reads as a dot, not as an arrow. */
export const MINIMUM_ARROW_LENGTH = 2;

export interface CandidateBoard {
  readonly board: BoardData;
  /** Reverse insertion order expressed as arrow ids: a valid solution by construction. */
  readonly constructionSolution: number[];
  readonly placementCount: number;
  readonly occupiedCells: number;
  readonly attempts: number;
  /** True when placement ran out of usable cells before reaching the target. */
  readonly exhausted: boolean;
  readonly repairPasses: number;
}

export interface BuildCandidateParams {
  readonly mask: Mask;
  readonly recipe: LevelRecipe;
  readonly rng: Pcg32;
  /** Target number of *cells* to cover, which is what board occupancy is measured in. */
  readonly targetOccupiedCells: number;
}

export function buildCandidate(params: BuildCandidateParams): CandidateBoard {
  const { mask, recipe, rng, targetOccupiedCells } = params;
  const width = mask.width;
  const height = mask.height;
  const size = width * height;

  const statics = computeCellStatics(mask);
  const strategyWeight = getCellWeightFn(recipe.placementStrategy);
  /**
   * Repair passes work the edges inwards. Late in a fill the only cells that can still host
   * an arrow are the ones with a short way out — a corridor near the silhouette edge is far
   * more likely to be clear than one crossing the whole board — so topping up from the rim
   * recovers noticeably more of a large board than uniform sampling.
   */
  const repairWeight: CellWeightFn = (cellIndex) =>
    1 + Math.max(0, 10 - statics.borderDistance[cellIndex]) * 4;

  /** -1 free, otherwise the index of the arrow occupying the cell. */
  const owner = new Int32Array(size).fill(-1);
  const occupied = new Uint8Array(size);
  const headDirection = new Int8Array(size).fill(-1);
  /**
   * How many placed arrows have this cell in their exit corridor. Laying a path here is
   * what blocks them, so it is the generator's lever on dependency depth.
   */
  const corridorCount = new Int32Array(size);
  const directionCounts = new Int32Array(4);
  const componentCounts = new Int32Array(Math.max(1, statics.componentCount));

  const placements: ArrowShape[] = [];
  let occupiedCells = 0;

  const inside = (x: number, y: number): boolean => x >= 0 && y >= 0 && x < width && y < height;
  const isFree = (x: number, y: number): boolean =>
    inside(x, y) && mask.cells[y * width + x] === 1 && owner[y * width + x] < 0;

  /**
   * Cells of the exit corridor: from just beyond the head to the board edge. Returns null
   * when any of them is already occupied, which is what makes the arrow legal on insertion.
   */
  const clearCorridor = (headX: number, headY: number, direction: Direction): number[] | null => {
    const { dx, dy } = DIRECTION_VECTORS[direction];
    const corridor: number[] = [];
    let x = headX + dx;
    let y = headY + dy;
    while (inside(x, y)) {
      const index = y * width + x;
      if (owner[index] >= 0) return null;
      corridor.push(index);
      x += dx;
      y += dy;
    }
    return corridor;
  };

  const context: StrategyContext = {
    statics,
    occupied,
    componentCounts,
    // A cheap proxy for "how boxed in is this cell": scanning all four corridors here
    // would make cell selection quadratic in board size.
    freeNeighbours: (cellIndex: number) => {
      const x = cellIndex % width;
      const y = (cellIndex - x) / width;
      let count = 0;
      if (isFree(x - 1, y)) count += 1;
      if (isFree(x + 1, y)) count += 1;
      if (isFree(x, y - 1)) count += 1;
      if (isFree(x, y + 1)) count += 1;
      return count;
    },
    get placedCount(): number {
      return placements.length;
    },
  };

  /**
   * Grows the tail backwards from the head. The first step is forced (the cell behind the
   * head, so the drawn head matches the facing); after that the walk prefers to keep going
   * straight or turn, biased by the recipe's winding weights.
   */
  const growTail = (
    headX: number,
    headY: number,
    direction: Direction,
    corridor: readonly number[],
    targetLength: number,
  ): CellPoint[] => {
    const forbidden = new Set<number>(corridor);
    const cells: CellPoint[] = [{ x: headX, y: headY }];
    forbidden.add(headY * width + headX);

    const behind = DIRECTION_VECTORS[direction];
    let currentX = headX - behind.dx;
    let currentY = headY - behind.dy;
    let incoming = direction;

    while (cells.length < targetLength && isFree(currentX, currentY)) {
      const index = currentY * width + currentX;
      if (forbidden.has(index)) break;
      cells.push({ x: currentX, y: currentY });
      forbidden.add(index);

      // Choose where the tail continues, away from the head.
      const options: Direction[] = [];
      const weights: number[] = [];
      for (const step of DIRECTIONS) {
        const vector = DIRECTION_VECTORS[step];
        const nextX = currentX + vector.dx;
        const nextY = currentY + vector.dy;
        if (!isFree(nextX, nextY)) continue;
        if (forbidden.has(nextY * width + nextX)) continue;
        // Never double back into the cell just added.
        if (step === oppositeDirection(incoming)) continue;

        options.push(step);
        const shape = step === incoming ? recipe.straightBias : recipe.turnBias;
        const crossing = recipe.blockingBias * Math.min(3, corridorCount[nextY * width + nextX]);
        // One step of lookahead: walking into a pocket ends the arrow early, so prefer cells
        // that still have somewhere to go. This is what lets long arrows survive dense boards.
        let freedom = 0;
        for (const ahead of DIRECTIONS) {
          const aheadVector = DIRECTION_VECTORS[ahead];
          const aheadX = nextX + aheadVector.dx;
          const aheadY = nextY + aheadVector.dy;
          if (!isFree(aheadX, aheadY)) continue;
          if (forbidden.has(aheadY * width + aheadX)) continue;
          freedom += 1;
        }
        weights.push(shape + crossing + freedom * 2);
      }
      if (options.length === 0) break;

      const chosen = options[rng.weightedIndex(weights, options.length)];
      const vector = DIRECTION_VECTORS[chosen];
      incoming = chosen;
      currentX += vector.dx;
      currentY += vector.dy;
    }

    // `cells` currently runs head -> tail; the model stores tail -> head.
    cells.reverse();
    return cells;
  };

  /**
   * Free cells directly behind a prospective head, capped. A facing with nothing behind it
   * can only ever produce a single-cell arrow, so this keeps long arrows achievable.
   */
  const roomBehind = (headX: number, headY: number, direction: Direction): number => {
    const { dx, dy } = DIRECTION_VECTORS[direction];
    let room = 0;
    let x = headX - dx;
    let y = headY - dy;
    while (room < 4 && isFree(x, y)) {
      room += 1;
      x -= dx;
      y -= dy;
    }
    return room;
  };

  const commit = (
    cells: readonly CellPoint[],
    direction: Direction,
    corridor: readonly number[],
  ): void => {
    const arrowIndex = placements.length;
    placements.push({ cells: [...cells], direction });
    for (const index of corridor) corridorCount[index] += 1;
    for (const cell of cells) {
      const index = cell.y * width + cell.x;
      owner[index] = arrowIndex;
      occupied[index] = 1;
    }
    const head = cells[cells.length - 1];
    headDirection[head.y * width + head.x] = DIRECTIONS.indexOf(direction);
    directionCounts[DIRECTIONS.indexOf(direction)] += 1;
    occupiedCells += cells.length;

    const component = statics.componentOf[cells[0].y * width + cells[0].x];
    if (component >= 0 && component < componentCounts.length) componentCounts[component] += 1;
  };

  let attempts = 0;

  const fill = (candidates: number[], weightFn: CellWeightFn, attemptBudget: number): void => {
    const weights: number[] = [];
    // Weights only depend on board state, and a failed attempt changes nothing — so they are
    // recomputed after a placement, not after every draw. Most attempts fail (a cell with no
    // clear corridor, or no room behind the head), and rescanning every candidate each time
    // dominated generation cost on large boards.
    let weightsStale = true;

    while (
      occupiedCells < targetOccupiedCells &&
      candidates.length > 0 &&
      attempts < attemptBudget
    ) {
      attempts += 1;

      if (weightsStale) {
        let total = 0;
        for (let i = 0; i < candidates.length; i += 1) {
          const cell = candidates[i];
          const base = owner[cell] >= 0 ? 0 : weightFn(cell, context);
          // Starting an arrow inside someone else's corridor is what creates dependencies.
          const boost =
            recipe.blockingBias > 0
              ? 1 + recipe.blockingBias * Math.min(3, corridorCount[cell])
              : 1;
          const safe = base > 0 ? base * boost : 0;
          weights[i] = safe;
          total += safe;
        }
        if (total === 0) {
          for (let i = 0; i < candidates.length; i += 1) {
            weights[i] = owner[candidates[i]] >= 0 ? 0 : 1;
            total += weights[i];
          }
          if (total === 0) break;
        }
        weightsStale = false;
      }

      const slot = rng.weightedIndex(weights, candidates.length);
      const headIndex = candidates[slot];
      candidates.splice(slot, 1);
      weights.splice(slot, 1);
      if (owner[headIndex] >= 0) continue;

      const headX = headIndex % width;
      const headY = (headIndex - headX) / width;

      // Which facings have a completely clear exit corridor from here?
      const facings: Direction[] = [];
      const corridors: number[][] = [];
      const facingWeights: number[] = [];
      for (const direction of DIRECTIONS) {
        const corridor = clearCorridor(headX, headY, direction);
        if (corridor === null) continue;
        // Every arrow is at least two cells: a lone head reads as a dot rather than as an
        // arrow, so a facing with nothing behind it is not a candidate at all.
        if (roomBehind(headX, headY, direction) < 1) continue;
        facings.push(direction);
        corridors.push(corridor);
        facingWeights.push(
          facingWeight(
            direction,
            headX,
            headY,
            corridor.length,
            roomBehind(headX, headY, direction),
            recipe,
            {
              width,
              height,
              directionCounts,
              headDirection,
            },
          ),
        );
      }
      if (facings.length === 0) continue;

      const choice = rng.weightedIndex(facingWeights, facings.length);
      const direction = facings[choice];
      const targetLength = pickLength(recipe, rng, targetOccupiedCells - occupiedCells);
      const cells = growTail(headX, headY, direction, corridors[choice], targetLength);
      // Defensive: `roomBehind` guarantees a second cell, but never commit a lone head.
      if (cells.length < MINIMUM_ARROW_LENGTH) continue;
      commit(cells, direction, corridors[choice]);
      weightsStale = true;
    }
  };

  const initialCells: number[] = [];
  for (let index = 0; index < size; index += 1) {
    if (mask.cells[index] === 1) initialCells.push(index);
  }
  fill(initialCells, strategyWeight, recipe.maxPlacementAttempts);
  const exhausted = occupiedCells < targetOccupiedCells;

  // Repair passes: a biased strategy can strand usable cells. Topping up with uniform
  // weighting recovers occupancy without changing any rule, and the fixed pass count keeps
  // the work bounded and identical on every device.
  let repairPasses = 0;
  while (repairPasses < recipe.maxRepairIterations && occupiedCells < targetOccupiedCells) {
    repairPasses += 1;
    const remaining: number[] = [];
    for (let index = 0; index < size; index += 1) {
      if (mask.cells[index] === 1 && owner[index] < 0) remaining.push(index);
    }
    if (remaining.length === 0) break;
    const before = occupiedCells;
    fill(remaining, repairWeight, recipe.maxPlacementAttempts * (repairPasses + 1));
    if (occupiedCells === before) break;
  }

  const board = normalizeBoard(width, height, Uint8Array.from(mask.cells), placements);

  // Map insertion order onto the normalised arrow ids, then reverse it.
  const cellMap = buildArrowCellMap(board);
  const constructionSolution: number[] = [];
  for (let i = placements.length - 1; i >= 0; i -= 1) {
    const head = placements[i].cells[placements[i].cells.length - 1];
    constructionSolution.push(cellMap[head.y * width + head.x]);
  }

  return {
    board,
    constructionSolution,
    placementCount: placements.length,
    occupiedCells,
    attempts,
    exhausted,
    repairPasses,
  };
}

/**
 * Path length for the next arrow. The remaining cell budget only truncates an arrow when
 * there is genuinely little room left: clamping every arrow to the exact remainder would
 * fill the tail end of a board with stubs.
 */
function pickLength(recipe: LevelRecipe, rng: Pcg32, cellsRemaining: number): number {
  const min = Math.max(MINIMUM_ARROW_LENGTH, recipe.arrowLengthRange[0]);
  const max = Math.max(min, recipe.arrowLengthRange[1]);
  const target = rng.nextInt(min, max);
  // Overshooting the cell budget by a cell or two beats ending a board with stubs.
  return cellsRemaining >= min ? target : min;
}

interface FacingState {
  readonly width: number;
  readonly height: number;
  readonly directionCounts: Int32Array;
  readonly headDirection: Int8Array;
}

/**
 * Context-sensitive facing choice. Uniform facings produce bland boards: the weights let a
 * recipe ask for long corridors, balanced directions, or heads that do not echo their
 * neighbours.
 */
function facingWeight(
  direction: Direction,
  headX: number,
  headY: number,
  corridorLength: number,
  roomBehind: number,
  recipe: LevelRecipe,
  state: FacingState,
): number {
  const heuristics = recipe.directionHeuristics;
  const maxDimension = Math.max(state.width, state.height);
  const directionIndex = DIRECTIONS.indexOf(direction);

  let maxDirectionCount = 0;
  for (let i = 0; i < 4; i += 1) {
    if (state.directionCounts[i] > maxDirectionCount) maxDirectionCount = state.directionCounts[i];
  }

  const balanceBonus = Math.min(3, maxDirectionCount - state.directionCounts[directionIndex]);
  const normalisedCorridor = Math.floor((corridorLength * 4) / maxDimension);
  const interior = corridorLength * 2 >= maxDimension ? 1 : 0;

  // Avoid a head echoing an immediate neighbour's facing.
  let echoes = 0;
  for (const neighbour of DIRECTIONS) {
    const vector = DIRECTION_VECTORS[neighbour];
    const x = headX + vector.dx;
    const y = headY + vector.dy;
    if (x < 0 || y < 0 || x >= state.width || y >= state.height) continue;
    if (state.headDirection[y * state.width + x] === directionIndex) echoes += 1;
  }

  // Room behind the head decides whether this arrow can be long at all, so it is weighted
  // in proportion to how long the recipe wants arrows to be.
  const wantsLength = recipe.arrowLengthRange[1] > 1 ? 1 : 0;

  return Math.max(
    1,
    heuristics.base +
      heuristics.edgeDistance * normalisedCorridor +
      heuristics.directionBalance * balanceBonus +
      heuristics.localRepetition * (echoes === 0 ? 1 : 0) +
      heuristics.towardsInterior * interior +
      wantsLength * roomBehind * 4,
  );
}
